import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from 'app/services/auth/auth.service';
import { CompanyService } from 'app/services/company/company.service';
import { HttpClient } from '@angular/common/http';
import { API_URL } from 'app/helpers/globals';

interface Terminal {
  terminalId: number;
  name: string;
  type: string;
  registrationNumber: string;
  terminalStatus: string;
  requested: boolean;
  replaced: boolean;
  rduid: string;
  selected?: boolean;
}

interface TerminalsResponse {
  result: string;
  message: string;
  error: any;
  terminals: Terminal[];
}

@Component({
  selector: 'app-device-replacement-warning-modal',
  templateUrl: './device-replacement-warning-modal.component.html',
  styleUrls: ['./device-replacement-warning-modal.component.css']
})
export class DeviceReplacementWarningModalComponent implements OnInit, OnDestroy {

  private subscriptions = new Subscription();
  hidden = true;
  showContactForm = false;

  // Contact form fields
  contactName = '';
  contactEmail = '';
  contactPhone = '';
  gpsInterested = false;

  // Vehicle/Terminal list
  terminals: Terminal[] = [];
  loadingTerminals = false;
  submittingRequest = false;

  @Output() openModalRequested = new EventEmitter<void>();

  constructor(
    private readonly translateService: TranslateService,
    private readonly authService: AuthService,
    private readonly companyService: CompanyService,
    private readonly http: HttpClient,
  ) { }

  ngOnInit(): void {
    // Check if user has terminals to replace from auth response
    const loggedInUser = this.authService.getLoggedInUser();
    if (loggedInUser && loggedInUser.HasTerminalsToReplace) {
      this.hidden = false;
      this.loadTerminals();
    }

    // Also subscribe to company flags changes
    this.subscriptions.add(
      this.authService.currentCompanyId.subscribe(companyId => {
        if (companyId > 0) {
          this.companyService.getCompanyFlags(companyId).subscribe(flags => {
            if (flags.hasTerminalsToReplace) {
              this.hidden = false;
              this.loadTerminals();
            }
          });
        }
      })
    );
  }

  loadTerminals() {
    // Get the currently selected company ID
    let clientId: number;
    this.authService.currentCompanyId.subscribe(id => {
      clientId = id;
    }).unsubscribe();

    if (!clientId || clientId <= 0) {
      console.error('No valid company ID found');
      return;
    }

    this.loadingTerminals = true;
    this.http.get<TerminalsResponse>(API_URL + `replace/get-all/${clientId}`, { withCredentials: true })
      .subscribe(
        (response) => {
          if (response.result === 'SUCCESS' && response.terminals) {
            // Filter out terminals that are already requested and replaced
            this.terminals = response.terminals
              .filter(t => !t.requested || !t.replaced)
              .map(t => ({
                ...t,
                selected: true // All terminals are selected by default
              }));
          }
          this.loadingTerminals = false;
        },
        (error) => {
          console.error('Error loading terminals', error);
          this.loadingTerminals = false;
        }
      );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  dismissModal() {
    this.hidden = true;
    this.showContactForm = false;
  }

  openContactForm() {
    this.showContactForm = true;
  }

  closeContactForm() {
    this.showContactForm = false;
    this.contactName = '';
    this.contactEmail = '';
    this.contactPhone = '';
    this.gpsInterested = false;
  }

  submitContactInfo() {
    // Get the currently selected company ID
    let clientId: number;
    this.authService.currentCompanyId.subscribe(id => {
      clientId = id;
    }).unsubscribe();

    if (!clientId || clientId <= 0) {
      console.error('No valid company ID found');
      return;
    }

    // Get selected terminal IDs and RDU IDs
    const selectedTerminals = this.terminals.filter(t => t.selected);
    const selectedTerminalIds = selectedTerminals.map(t => t.terminalId);
    const selectedRduIds = selectedTerminals.map(t => t.rduid);

    // Payload for contact info with GPS interest information
    const contactPayload = {
      clientId: clientId,
      contactName: this.contactName,
      contactEmail: this.contactEmail,
      contactPhone: this.contactPhone,
      terminalIds: selectedTerminalIds,
      interestedInGPSTracking: this.gpsInterested
    };

    // Payload for vehicle replacement request
    const replacementPayload = {
      rduIds: selectedRduIds,
      clientId: clientId
    };

    // Set loading state
    this.submittingRequest = true;

    // Call both APIs
    this.http.post(API_URL + 'replace/save-contact-info', contactPayload, { withCredentials: true })
      .subscribe(
        (contactResponse) => {
          console.log('Contact info saved successfully', contactResponse);

          // Now submit the replacement request
          this.http.post(API_URL + 'replace/change-request', replacementPayload, { withCredentials: true })
            .subscribe(
              (replacementResponse) => {
                console.log('Replacement request submitted successfully', replacementResponse);
                this.submittingRequest = false;
                // Close the form and modal
                this.closeContactForm();
                this.dismissModal();
              },
              (error) => {
                console.error('Error submitting replacement request', error);
                this.submittingRequest = false;
                // Still close the modal as contact info was saved
                this.closeContactForm();
                this.dismissModal();
              }
            );
        },
        (error) => {
          console.error('Error saving contact info', error);
          this.submittingRequest = false;
          // You might want to show an error message to the user here
        }
      );
  }

  public openModal() {
    this.hidden = false;
    // Load terminals when modal is opened manually
    if (this.terminals.length === 0) {
      this.loadTerminals();
    }
  }

  toggleTerminalSelection(terminal: Terminal) {
    terminal.selected = !terminal.selected;
  }

  getDisplayName(terminal: Terminal): string {
    // Use registration number if available, otherwise use name
    return terminal.registrationNumber || terminal.name;
  }

  isFormValid(): boolean {
    return this.contactName.trim() !== '' &&
           this.isValidEmail(this.contactEmail) &&
           this.isValidPhone(this.contactPhone);
  }

  isValidEmail(email: string): boolean {
    if (!email || email.trim() === '') {
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  }

  isValidPhone(phone: string): boolean {
    if (!phone || phone.trim() === '') {
      return false;
    }
    // Allow digits, spaces, hyphens, parentheses, and plus sign
    // Minimum 7 digits for a valid phone number
    const phoneRegex = /^[\d\s\-\(\)\+]{7,}$/;
    const digitCount = phone.replace(/\D/g, '').length;
    return phoneRegex.test(phone) && digitCount >= 7;
  }

  getEmailError(): string {
    if (!this.contactEmail || this.contactEmail.trim() === '') {
      return '';
    }
    if (!this.isValidEmail(this.contactEmail)) {
      return 'Please enter a valid email address';
    }
    return '';
  }

  getPhoneError(): string {
    if (!this.contactPhone || this.contactPhone.trim() === '') {
      return '';
    }
    if (!this.isValidPhone(this.contactPhone)) {
      return 'Please enter a valid phone number (minimum 7 digits)';
    }
    return '';
  }
}
